import { SupplierInventoryDeltaResponseDto } from "../types";
import {
  applyAdjustment,
  PartConfirmationBucketEntry,
  SelectedSupplier,
  SupplierPart,
} from "./partsCalculationUtils";

/**
 * Build the composite key used to look up a part-confirmation quantity
 * for a specific supplier + root part family.
 */
export const getSupplierPartConfirmationKey = (
  supplierId: string,
  rootPartNumber: string,
): string =>
  `${String(supplierId ?? "").trim()}::${String(rootPartNumber ?? "").trim()}`;

export const PART_CONFIRMATION_ADJUSTMENT_LABEL = "Part Confirmation";

const alreadyReservedQty = (part: SupplierPart): number =>
  (part.adjustments ?? [])
    .filter((adjustment) => adjustment.label === PART_CONFIRMATION_ADJUSTMENT_LABEL)
    .reduce((sum, adjustment) => sum + (Number(adjustment.value) || 0), 0);

const reservationQtyByRootFromBucket = (
  supplierId: string,
  partConfirmationBucket?: PartConfirmationBucketEntry[],
): Map<string, number> => {
  const qtyByRoot = new Map<string, number>();
  const id = String(supplierId ?? "").trim();
  if (!id || !partConfirmationBucket?.length) return qtyByRoot;

  for (const entry of partConfirmationBucket) {
    if (String(entry.supplierId ?? "").trim() !== id) continue;
    for (const part of entry.confirmationParts ?? []) {
      const qty = Math.max(0, Number(part.askedQty) || 0);
      if (qty <= 0) continue;
      const rootPartNumber = String(part.rootPartNumber ?? "").trim();
      const partNumber = String(part.partNumber ?? "").trim();
      const roots = [rootPartNumber];
      if (partNumber && partNumber !== rootPartNumber) {
        roots.push(partNumber);
      }
      for (const root of roots.filter(Boolean)) {
        qtyByRoot.set(root, (qtyByRoot.get(root) ?? 0) + qty);
      }
    }
  }
  return qtyByRoot;
};

/**
 * Apply part-confirmation quantities to supplier parts.
 *
 * This is the LAST quantity adjustment in the pipeline:
 *   Typesense parts → offline merge → delta → THIS → metrics
 *
 * For each part, the adjusted quantity is:
 *   Math.max(0, familyQuantity - confirmationQuantity)
 *
 * Unsent PC-bucket asked qty is applied even when the API map has no pending
 * row yet, so leftover stock is 0 after a full assign (1 stock − 1 bucketed).
 *
 * Parts without a matching confirmation entry are returned unchanged.
 */
export const applySupplierPartConfirmationToParts = <T extends SupplierPart>({
  supplierId,
  parts,
  supplierPartConfirmationQuantityMap,
  partConfirmationBucket,
}: {
  supplierId: string;
  parts: T[];
  supplierPartConfirmationQuantityMap?: Map<string, number>;
  partConfirmationBucket?: PartConfirmationBucketEntry[];
}): T[] => {
  const id = String(supplierId ?? "").trim();
  const bucketReservations = reservationQtyByRootFromBucket(
    id,
    partConfirmationBucket,
  );
  if (
    !id ||
    (!supplierPartConfirmationQuantityMap?.size && bucketReservations.size === 0)
  ) {
    return parts;
  }

  return parts.map((part) => {
    const rootPartNumber = String(part.rootPartNumber ?? "").trim();
    if (!rootPartNumber) return part;

    const mapQuantity =
      supplierPartConfirmationQuantityMap?.get(
        getSupplierPartConfirmationKey(id, rootPartNumber),
      ) ??
      supplierPartConfirmationQuantityMap?.get(
        getSupplierPartConfirmationKey(supplierId, part.rootPartNumber),
      ) ??
      0;
    const bucketQuantity = bucketReservations.get(rootPartNumber) ?? 0;
    // API map is global confirmed+pending (other orders too). Unsent bucket
    // qty for this enquiry must be added, not maxed — max(2, 1) left stock at 1.
    const supplierPartConfirmationQuantity = mapQuantity + bucketQuantity;
    const alreadyApplied = alreadyReservedQty(part);
    const remainingToApply = Math.max(
      0,
      supplierPartConfirmationQuantity - alreadyApplied,
    );

    if (remainingToApply <= 0) return part;

    const familyQuantity = Number(part.familyQuantity) || 0;
    const adjustedFamilyQuantity = Math.max(0, familyQuantity - remainingToApply);

    return applyAdjustment(
      part,
      { label: PART_CONFIRMATION_ADJUSTMENT_LABEL, value: remainingToApply },
      adjustedFamilyQuantity,
    );
  });
};

/**
 * Build a lookup map from the deltas API response.
 *
 * Key:   "supplierId::rootPartNumber"
 * Value: confirmedQuantity + pendingConfirmationQuantity
 *
 * Returns an empty map if response is undefined or has no results.
 */
export const buildSupplierPartConfirmationQuantityMap = (
  response: SupplierInventoryDeltaResponseDto | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!response?.results) return map;

  for (const delta of response.results) {
    if (!delta.supplierId || !delta.rootPartNumber) continue;
    const confirmationQuantity =
      (Number(delta.confirmedQuantity) || 0) +
      (Number(delta.pendingConfirmationQuantity) || 0);
    if (confirmationQuantity <= 0) continue;
    map.set(
      getSupplierPartConfirmationKey(delta.supplierId, delta.rootPartNumber),
      confirmationQuantity,
    );
  }
  return map;
};

const addQtyOnMap = (
  map: Map<string, number>,
  supplierId: string,
  rootPartNumber: string,
  quantity: number,
): void => {
  const root = String(rootPartNumber ?? "").trim();
  const id = String(supplierId ?? "").trim();
  const qty = Math.max(0, Number(quantity) || 0);
  if (!id || !root || qty <= 0) return;
  const key = getSupplierPartConfirmationKey(id, root);
  map.set(key, (map.get(key) ?? 0) + qty);
};

/**
 * Reduce discoverable stock by qty already staged in Confirmed + PC buckets
 * so a supplier with 10 stock and 8 in a bucket shows as 2, not 10.
 */
export const overlayDiscoveryBucketReservations = (
  baseMap: Map<string, number>,
  confirmedSuppliers: SelectedSupplier[],
  partConfirmationBucket: PartConfirmationBucketEntry[],
): Map<string, number> => {
  const bucketReservations = new Map<string, number>();

  for (const supplier of confirmedSuppliers) {
    for (const part of supplier.selectedParts ?? []) {
      addQtyOnMap(
        bucketReservations,
        supplier.supplierId,
        part.rootPartNumber,
        part.familyQuantity,
      );
    }
  }

  for (const entry of partConfirmationBucket) {
    for (const part of entry.confirmationParts ?? []) {
      addQtyOnMap(
        bucketReservations,
        entry.supplierId,
        part.rootPartNumber,
        part.askedQty,
      );
    }
  }

  const map = new Map(baseMap);
  bucketReservations.forEach((qty, key) => {
    map.set(key, Math.max(map.get(key) ?? 0, qty));
  });
  return map;
};

/**
 * Declined PC responses must not contribute stock to discovery metrics.
 * Reserves all availability for those roots (available → 0 after apply).
 * Does not alter how Confirmed/PC bucket reservations are calculated.
 */
export const overlayDeclinedPartConfirmationsOnQuantityMap = (
  baseMap: Map<string, number>,
  responsesBySupplierId: Map<
    string,
    Array<{
      partId?: string;
      rootPartNumber?: string;
      status?: string;
      responseStatus?: string;
    }>
  >,
  originalParts: Array<{ partId?: string; rootPartNumber: string }> = [],
): Map<string, number> => {
  const rootByPartId = new Map(
    originalParts
      .filter((part) => part.partId)
      .map((part) => [String(part.partId), part.rootPartNumber]),
  );
  const map = new Map(baseMap);

  responsesBySupplierId.forEach((parts, supplierId) => {
    for (const part of parts ?? []) {
      const status = String(part.responseStatus ?? part.status ?? "")
        .trim()
        .toLowerCase();
      if (status !== "declined") continue;
      const root =
        String(part.rootPartNumber ?? "").trim() ||
        rootByPartId.get(String(part.partId ?? "")) ||
        "";
      if (!root || !supplierId) continue;
      map.set(
        getSupplierPartConfirmationKey(String(supplierId), root),
        Number.MAX_SAFE_INTEGER,
      );
    }
  });

  return map;
};
